import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { MOCK_MODE, mockUser } from '@/lib/mock'
import type { User } from 'firebase/auth'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!user.value)
  const userEmail = computed(() => user.value?.email ?? null)
  const userName = computed(() => user.value?.displayName ?? user.value?.email ?? 'User')
  const userPhoto = computed(() => user.value?.photoURL ?? null)

  // Initialize auth
  async function init() {
    if (MOCK_MODE) {
      // Auto-login in mock mode
      user.value = mockUser as unknown as User
      localStorage.setItem('token', 'mock-token')
      loading.value = false
      return
    }

    // Real Firebase auth
    const { auth, signInWithGoogle: _, signOutUser: __ } = await import('@/lib/firebase')
    const { onAuthStateChanged } = await import('firebase/auth')

    onAuthStateChanged(auth, async (firebaseUser) => {
      user.value = firebaseUser
      loading.value = false

      if (firebaseUser) {
        const token = await firebaseUser.getIdToken()
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    })
  }

  async function login() {
    if (MOCK_MODE) {
      user.value = mockUser as unknown as User
      localStorage.setItem('token', 'mock-token')
      return
    }

    loading.value = true
    error.value = null
    try {
      const { signInWithGoogle } = await import('@/lib/firebase')
      const firebaseUser = await signInWithGoogle()
      user.value = firebaseUser
      const token = await firebaseUser.getIdToken()
      localStorage.setItem('token', token)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Login failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    if (MOCK_MODE) {
      user.value = null
      localStorage.removeItem('token')
      return
    }

    loading.value = true
    try {
      const { signOutUser } = await import('@/lib/firebase')
      await signOutUser()
      user.value = null
      localStorage.removeItem('token')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Logout failed'
    } finally {
      loading.value = false
    }
  }

  async function refreshToken() {
    if (MOCK_MODE) return
    if (user.value) {
      const token = await user.value.getIdToken(true)
      localStorage.setItem('token', token)
    }
  }

  // Auto-init
  init()

  return {
    user,
    loading,
    error,
    isAuthenticated,
    userEmail,
    userName,
    userPhoto,
    login,
    logout,
    refreshToken
  }
})
